import { useState, useEffect, useCallback } from "react";
import { useTheme, DARK, LIGHT } from "../../../components/Layout";
import { accountsAPI, journalAPI } from "../../../services/api";
import { usePermission } from "../../../hooks/usePermission";
import { useSubmit } from "../../../hooks/useSubmit";
import SearchableSelect from "../../../components/SearchableSelect";
import PersianDatePicker, { todayGregorian, gregorianToJalaliStr, getTodayJalali, jalaliToGregorianStr } from "../../../utils/PersianDatePicker";
import { generateAccountExcelReport, generateAccountEntitiesExcelReport } from "../../../utils/excelReport";
import {
  Plus, Edit2, Trash2, EyeOff, Eye, FileText,
  ChevronDown, ChevronLeft, ChevronRight, ChevronsRight, ChevronsLeft, X, Printer, Search,
  Wallet, Coins, Building2, TrendingUp, TrendingDown, Scale, PieChart,
} from "lucide-react";

interface Account {
  id: number;
  code: string;
  name: string;
  type: string;
  currency: string;
  parent_id: number | null;
  balance: number;
  description: string;
  is_active: number;
  created_at?: string;
  opening_balance?: number | null;  // 🆕 از جدول جدید account_opening_balances
  opening_date?: string | null;     // 🆕 تاریخ بیلانس اولیه
}

const TYPES: Record<string, string> = {
  cash_asset:     "دارایی نقدی",
  non_cash_asset: "سرمایه جاری",
  fixed_asset:    "دارایی ثابت",
  income:         "عواید",
  expense:        "مصارف",
  liability:      "قرضداری",
  equity:         "سهم شرکا",
};

const TYPE_COLORS: Record<string, string> = {
  cash_asset:     "#10b981",
  non_cash_asset: "#3b82f6",
  fixed_asset:    "#0891b2",
  income:         "#8b5cf6",
  expense:        "#ef4444",
  liability:      "#f59e0b",
  equity:         "#06b6d4",
};

// 🆕 آیکون ثابت برای هر نوعیت حساب — تا چشم کاربر با رنگ+آیکون هر سطح حساب آشنا شود
const TYPE_ICONS: Record<string, any> = {
  cash_asset:     Wallet,
  non_cash_asset: Coins,
  fixed_asset:    Building2,
  income:         TrendingUp,
  expense:        TrendingDown,
  liability:      Scale,
  equity:         PieChart,
};

const CURRENCIES = ["USD", "AFN", "IRR", "TRY", "PKR", "EUR"];

const fmt = (n: number, cur?: string) => {
  const s = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(n || 0));
  return (n < 0 ? "-" : "") + s + (cur ? " " + cur : "");
};

// 🆕 پالت و اجزای بصری — تمِ جدید این صفحه (الگوگرفته از داشبوردهای فین‌تک حرفه‌ای مثل Stripe/Linear/Mercury)
const colHeadStyle: React.CSSProperties = { fontSize: 11, fontWeight: 700, letterSpacing: "0.02em", opacity: 0.65 };

const TypeBadge = ({ type }: { type: string }) => {
  const color = TYPE_COLORS[type] || "#6b7280";
  const Icon = TYPE_ICONS[type] || Wallet;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "4px 10px 4px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700,
      background: `${color}16`, color, whiteSpace: "nowrap", width: "fit-content",
      border: `1px solid ${color}2a`,
    }}>
      <Icon size={12} strokeWidth={2.4} />
      {TYPES[type] || type}
    </span>
  );
};

// 🆕 نشان دایره‌ای رنگی+آیکون کنار نام هر حساب — راهنمای بصری سریع برای تشخیص نوعیت حساب
const TypeAvatar = ({ type, size = 30 }: { type: string; size?: number }) => {
  const color = TYPE_COLORS[type] || "#6b7280";
  const Icon = TYPE_ICONS[type] || Wallet;
  return (
    <span style={{
      display: "flex", alignItems: "center", justifyContent: "center",
      width: size, height: size, borderRadius: size * 0.34, flexShrink: 0,
      background: `linear-gradient(135deg, ${color}26, ${color}0d)`,
      border: `1px solid ${color}35`, color,
    }}>
      <Icon size={Math.round(size * 0.52)} strokeWidth={2.2} />
    </span>
  );
};

export default function AccountsPage() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  // 🆕 توکن‌های تمِ محلیِ این صفحه
  const ACCENT = "#4f46e5";
  const ACCENT_GRAD = "linear-gradient(135deg,#4f46e5,#7c3aed)";
  const SURFACE = dark ? "rgba(255,255,255,0.025)" : "#ffffff";
  const BORDER = dark ? "rgba(255,255,255,0.08)" : "#e9ecf2";
  const POS = "#059669";
  const NEG = "#dc2626";
  const pagerBtnStyle = (disabled: boolean): React.CSSProperties => ({
    width: 30, height: 30, borderRadius: 8, border: `1px solid ${BORDER}`,
    background: dark ? "rgba(255,255,255,0.03)" : "#fff",
    color: disabled ? T.subtext : T.text, cursor: disabled ? "default" : "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", opacity: disabled ? 0.4 : 1,
    transition: "background 0.15s, border-color 0.15s",
  });
  const canAdd    = usePermission("finance", "add");
  const canEdit   = usePermission("finance", "edit");
  const canDelete = usePermission("finance", "delete");
  const { submitting, submit } = useSubmit();

  const [accounts, setAccounts]       = useState<Account[]>([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState("");
  const [showSuggest, setShowSuggest] = useState(false);
  const [typeFilter, setTypeFilter]   = useState("");
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [showAdd, setShowAdd]         = useState(false);
  const [editAcc, setEditAcc]         = useState<Account | null>(null);
  const [reportAcc, setReportAcc]     = useState<Account | null>(null);
  const [reportData, setReportData]   = useState<any[]>([]);
  const [reportEntities, setReportEntities] = useState<any[]>([]);
  const [reportAutoLink, setReportAutoLink] = useState<string | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportDateFrom, setReportDateFrom] = useState("");
  const [reportDateTo,   setReportDateTo]   = useState("");
  const [toast, setToast]             = useState({ text: "", ok: true });

  const [form, setForm] = useState({
    name: "", type: "", currency: "", parent_id: "",
    opening_balance: "0", created_date: todayGregorian(), description: "",
  });
  const [editForm, setEditForm] = useState({
    name: "",
    description: "",
    type: "",
    currency: "",
    parent_id: "",
    opening_balance: "0",
    opening_date: todayGregorian(),
    change_opening_balance: false,  // 🆕 فقط وقتی true شد، بیلانس اولیه به سرور فرستاده می‌شود
    adjustment_note: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    const res = await accountsAPI.list();
    if (res.success) { setAccounts(res.accounts); setExpanded(new Set(res.accounts.filter((a: any) => !a.parent_id).map((a: any) => a.id))); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = (text: string, ok = true) => {
    setToast({ text, ok });
    setTimeout(() => setToast({ text: "", ok: true }), 3000);
  };

  const parents   = accounts.filter(a => !a.parent_id);
  const children  = (pid: number) => accounts.filter(a => a.parent_id === pid);

  const parentOptions = parents
    .filter(a =>
      (!form.type || a.type === form.type) &&
      (!form.currency || a.currency === form.currency)
    )
    .map(a => ({ value: String(a.id), label: `${a.code} — ${a.name}` }));

  // برای فرم ویرایش — حساب خودش را از لیست مادران ممکن حذف کن
  const editParentOptions = parents
    .filter(a =>
      a.id !== (editAcc?.id || 0) &&  // خود حساب نمی‌تواند مادر خودش باشد
      (!editForm.type || a.type === editForm.type) &&
      (!editForm.currency || a.currency === editForm.currency)
    )
    .map(a => ({ value: String(a.id), label: `${a.code} — ${a.name}` }));

  const norm = (s: string) => (s || "").toLowerCase().trim();
  // 🆕 جستجو هم روی حساب مادر و هم روی زیرمجموعه‌هایش انجام می‌شود — اگر فقط زیرمجموعه مطابقت داشت، مادرش در لیست می‌ماند
  const accMatches = (a: any) => norm(a.name).includes(norm(search)) || norm(a.code).includes(norm(search));
  const filtered = parents
    .filter(a =>
      (!search || accMatches(a) || children(a.id).some(accMatches)) &&
      (!typeFilter || a.type === typeFilter)
    )
    // 🆕 ترتیب نمایش همیشه بر اساس شماره (کد) حساب است
    .sort((a, b) => (a.code || "").localeCompare(b.code || "", undefined, { numeric: true, sensitivity: "base" }));

  const searchSuggestions = search.trim()
    ? accounts.filter(accMatches).slice(0, 8)
    : [];

  // 🆕 هنگام جستجو، مادرِ زیرمجموعه‌ی مطابقت‌یافته به‌صورت خودکار باز می‌شود تا نتیجه بدون کلیک اضافه دیده شود
  useEffect(() => {
    if (!search.trim()) return;
    const toOpen = parents.filter(p => !accMatches(p) && children(p.id).some(accMatches)).map(p => p.id);
    if (toOpen.length) setExpanded(prev => new Set([...Array.from(prev), ...toOpen]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, accounts]);

  // 🆕 هر صفحه دقیقاً ۵۰ حساب نمایش می‌دهد (ثابت)
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [search, typeFilter]);

  // 🆕 صفحه‌بندی بر اساس تعداد واقعی ردیف‌های نمایش‌داده‌شده (مادر + زیرمجموعه‌های بازشده)
  // تا شمار حساب‌های دیده‌شده در هر صفحه دقیقاً ۵۰ تا باشد — ترتیب بر اساس شماره حساب.
  // هر گروه (مادر+زیرمجموعه‌هایش) یک واحد است و بین دو صفحه شکسته نمی‌شود.
  type Group = { acc: Account; kids: Account[] };
  const groups: Group[] = filtered.map(acc => ({ acc, kids: expanded.has(acc.id) ? children(acc.id) : [] }));
  const pages: Group[][] = [];
  { let current: Group[] = []; let count = 0;
    for (const g of groups) {
      const size = 1 + g.kids.length;
      if (count > 0 && count + size > PAGE_SIZE) { pages.push(current); current = []; count = 0; }
      current.push(g); count += size;
    }
    if (current.length) pages.push(current);
  }
  const totalPages = Math.max(1, pages.length);
  const safePage = Math.min(page, totalPages);
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [totalPages, page]);
  const pagedGroups = pages[safePage - 1] || [];

  const handleAdd = async () => {
    if (!form.name || !form.type || !form.currency) {
      showToast("نام، نوعیت و ارز الزامی است", false); return;
    }
    const res = await accountsAPI.add({
      name:            form.name,
      type:            form.type,
      currency:        form.currency,
      parent_id:       form.parent_id ? parseInt(form.parent_id) : null,
      opening_balance: parseFloat(form.opening_balance || "0"),
      created_date:    form.created_date,
      description:     form.description,
    });
    if (res.success) {
      showToast(`حساب با کد ${res.code} ایجاد شد`);
      setShowAdd(false);
      setForm({ name: "", type: "", currency: "", parent_id: "", opening_balance: "0", created_date: todayGregorian(), description: "" });
      load();
    } else {
      showToast(res.message || "خطا در ایجاد حساب", false);
    }
  };

  const handleEdit = async () => {
    if (!editAcc) return;
    if (!editForm.name) { showToast("نام الزامی است", false); return; }

    // ساخت payload — فقط فیلدهایی که قابل ویرایش هستند
    const payload: any = {
      id: editAcc.id,
      name: editForm.name,
      description: editForm.description,
      type: editForm.type,
      currency: editForm.currency,
      parent_id: editForm.parent_id ? parseInt(editForm.parent_id) : null,
    };

    // 🆕 بیلانس اولیه فقط زمانی فرستاده می‌شود که کاربر صریحاً فعالش کرده باشد
    // این تضمین می‌کند که اگر کاربر فقط نام/توضیحات را تغییر داد،
    // هیچ رکوردی در جدول opening_balance ساخته/تغییر نمی‌کند
    if (editForm.change_opening_balance) {
      payload.opening_balance = parseFloat(editForm.opening_balance || "0");
      payload.opening_date = editForm.opening_date;
      payload.adjustment_note = editForm.adjustment_note;
    }

    const res = await accountsAPI.update(payload);
    if (res.success) {
      showToast("حساب ویرایش شد");
      setEditAcc(null);
      load();
    } else {
      showToast(res.message || "خطا", false);
    }
  };

  // باز کردن مودال ویرایش با مقادیر فعلی حساب
  const openEditModal = (acc: Account) => {
    setEditAcc(acc);
    setEditForm({
      name: acc.name,
      description: acc.description || "",
      type: acc.type,
      currency: acc.currency,
      parent_id: acc.parent_id ? String(acc.parent_id) : "",
      // اگر در جدول جدید بیلانس اولیه نداشت، خالی نشون بده تا کاربر متوجه بشه
      opening_balance: acc.opening_balance != null ? String(acc.opening_balance) : "0",
      opening_date: acc.opening_date || acc.created_at?.split(" ")[0] || todayGregorian(),
      change_opening_balance: false,  // 🆕 به‌صورت پیش‌فرض دست نزن
      adjustment_note: "",
    });
  };

  const handleDelete = async (acc: Account) => {
    if (!window.confirm(`حذف حساب "${acc.name}"؟`)) return;
    const res = await accountsAPI.delete(acc.id);
    if (res.success) { showToast("حساب حذف شد"); load(); }
    else showToast(res.message || "خطا", false);
  };

  const handleToggle = async (acc: Account) => {
    const res = await accountsAPI.toggleStatus(acc.id);
    if (res.success) load();
    else showToast(res.message || "خطا", false);
  };

  const handleReport = async (acc: Account, dateFrom?: string, dateTo?: string) => {
    setReportAcc(acc);
    setReportLoading(true);
    setReportData([]);
    setReportEntities([]);
    setReportAutoLink(null);
    const res = await accountsAPI.report(acc.id, dateFrom || reportDateFrom, dateTo || reportDateTo);
    if (res.success) {
      setReportData(res.transactions || []);
      setReportEntities(res.entities || []);
      setReportAutoLink(res.auto_link || null);
      if (res.account) setReportAcc(res.account);
    }
    setReportLoading(false);
  };
  const [excelLoading, setExcelLoading] = useState(false);
  const handleExcelDownload = async () => {
    if (!reportAcc) return;
    setExcelLoading(true);
    try {
      const res = await accountsAPI.excelReport(reportAcc.id, reportDateFrom, reportDateTo);
      if (res.success) {
        if (res.auto_link) {
          await generateAccountEntitiesExcelReport(res.account, res.entities || []);
        } else {
          await generateAccountExcelReport(res.account, res.transactions || [], reportDateFrom, reportDateTo);
        }
      } else {
        showToast(res.message || "خطا در تولید اکسل", false);
      }
    } catch (e) {
      showToast("خطا در تولید فایل اکسل", false);
    }
    setExcelLoading(false);
  };

  const applyDateFilter = () => {
    if (reportAcc) handleReport(reportAcc, reportDateFrom, reportDateTo);
  };

  const clearDateFilter = () => {
    setReportDateFrom("");
    setReportDateTo("");
    if (reportAcc) handleReport(reportAcc, "", "");
  };

  const setQuickDate = (type: string) => {
    const today = new Date();
    let from = "", to = todayGregorian();
    const pad = (n: number) => String(n).padStart(2, "0");
    const fmt2 = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
    switch(type) {
      case "today":
        from = to = todayGregorian(); break;
      case "week": {
        const d = new Date(today); d.setDate(today.getDate() - 6);
        from = fmt2(d); break;
      }
      case "month": {
        const d = new Date(today.getFullYear(), today.getMonth(), 1);
        from = fmt2(d); break;
      }
      case "3month": {
        const d = new Date(today); d.setMonth(today.getMonth() - 3);
        from = fmt2(d); break;
      }
      case "6month": {
        const d = new Date(today); d.setMonth(today.getMonth() - 6);
        from = fmt2(d); break;
      }
      case "year": {
        const d = new Date(today.getFullYear(), 0, 1);
        from = fmt2(d); break;
      }
    }
    setReportDateFrom(from);
    setReportDateTo(to);
    if (reportAcc) handleReport(reportAcc, from, to);
  };

  const toggleExpand = (id: number) => {
    setExpanded(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const printReport = () => {
    if (!reportAcc) return;
    const w = window.open("", "_blank");
    if (!w) return;

    if (reportAutoLink) {
      const sorted = [...reportEntities].sort((a: any, b: any) => Number(b.balance) - Number(a.balance));
      const fmtN2 = (n: number) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(n));
      const rowsHtml = sorted.length === 0
        ? `<tr><td colspan="6" style="text-align:center;padding:40px;color:#94a3b8;font-size:13px">هیچ طلب‌داری با بیلانس غیر صفر یافت نشد</td></tr>`
        : sorted.map((en: any, i: number) => `
          <tr class="${i % 2 === 0 ? "row-even" : "row-odd"}">
            <td class="center" style="color:#94a3b8;font-size:10px">${i + 1}</td>
            <td class="center" style="color:#7c3aed;font-weight:700;font-size:11px">${en.code || "—"}</td>
            <td style="font-weight:700">${en.name || "—"}</td>
            <td class="center" style="direction:ltr">${en.phone || "—"}</td>
            <td style="font-size:10px;color:#475569">${[en.city, en.notes].filter(Boolean).join(" — ") || "—"}</td>
            <td class="center" style="font-weight:800;font-size:13px;color:${Number(en.balance) >= 0 ? "#059669" : "#dc2626"};direction:ltr">${fmtN2(Number(en.balance))}</td>
          </tr>`).join("");
      const totalBal = sorted.reduce((s: number, e: any) => s + Number(e.balance || 0), 0);
      w.document.write(`<!DOCTYPE html>
<html dir="rtl" lang="fa">
<head>
  <meta charset="UTF-8">
  <title>گزارش حساب — ${reportAcc.name}</title>
  <link href="https://fonts.googleapis.com/css2?family=Vazirmatn:wght@300;400;600;700;800&display=swap" rel="stylesheet">
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    @page { size: A4 landscape; margin: 12mm 14mm; }
    body { font-family: 'Vazirmatn', Tahoma, sans-serif; direction: rtl; font-size: 11px; color: #1e293b; background: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .page { padding: 20px; }
    .header { display: flex; justify-content: space-between; align-items: center; padding-bottom: 16px; border-bottom: 3px solid #3b82f6; margin-bottom: 16px; }
    .header-right { display: flex; align-items: center; gap: 14px; }
    .logo { width: 46px; height: 46px; background: linear-gradient(135deg, #3b82f6, #3b82f699); border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 22px; }
    .header-title { font-size: 20px; font-weight: 800; color: #0f172a; }
    .header-sub { font-size: 11px; color: #64748b; margin-top: 2px; }
    .header-left { text-align: left; }
    .badge { display: inline-block; background: #3b82f6; color: #fff; font-size: 10px; font-weight: 700; padding: 3px 12px; border-radius: 20px; margin-bottom: 4px; }
    .print-date { font-size: 10px; color: #94a3b8; }
    .info-row { display: flex; gap: 8px; margin-bottom: 16px; flex-wrap: wrap; }
    .info-card { flex: 1; min-width: 100px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 8px 12px; }
    .info-card.accent { border-color: #3b82f650; background: #3b82f608; }
    .info-label { font-size: 9px; color: #94a3b8; font-weight: 700; margin-bottom: 3px; letter-spacing: 0.5px; }
    .info-value { font-size: 13px; font-weight: 800; color: #0f172a; }
    .table-title { font-size: 12px; font-weight: 800; color: #374151; margin-bottom: 8px; padding-right: 8px; border-right: 3px solid #3b82f6; }
    table { width: 100%; border-collapse: collapse; font-size: 10.5px; border-radius: 10px; overflow: hidden; }
    thead tr { background: linear-gradient(135deg, #1e293b, #334155); }
    th { padding: 9px 8px; text-align: right; font-weight: 700; color: #f1f5f9; white-space: nowrap; font-size: 10px; }
    th.center { text-align: center; }
    .row-even { background: #ffffff; }
    .row-odd  { background: #f8fafc; }
    td { padding: 7px 8px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
    td.center { text-align: center; }
    tr:last-child td { border-bottom: 2px solid #e2e8f0; }
    .footer { margin-top: 20px; padding-top: 12px; border-top: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: flex-end; }
    .footer-note { font-size: 9.5px; color: #94a3b8; }
    .footer-sig { font-size: 10px; color: #64748b; border-top: 1px dashed #cbd5e1; padding-top: 6px; min-width: 160px; text-align: center; }
    @media print { body { font-size: 10px; } thead { display: table-header-group; } tr { page-break-inside: avoid; } }
  </style>
</head>
<body><div class="page">
  <div class="header">
    <div class="header-right">
      <div class="logo">🏦</div>
      <div>
        <div class="header-title">${reportAcc.name}</div>
        <div class="header-sub">گزارش لیست اشخاص — بیلانس هرکدام</div>
      </div>
    </div>
    <div class="header-left">
      <div><span class="badge">${TYPES[reportAcc.type] || reportAcc.type}</span></div>
      <div class="print-date">تاریخ چاپ: ${new Date().toLocaleDateString("fa-IR")} — ${new Date().toLocaleTimeString("fa-IR")}</div>
    </div>
  </div>
  <div class="info-row">
    <div class="info-card accent"><div class="info-label">کد حساب</div><div class="info-value">${reportAcc.code}</div></div>
    <div class="info-card"><div class="info-label">ارز</div><div class="info-value">${reportAcc.currency}</div></div>
    <div class="info-card"><div class="info-label">جمع بیلانس</div><div class="info-value" style="color:${totalBal>=0?'#059669':'#dc2626'}">${fmtN2(totalBal)} ${reportAcc.currency}</div></div>
    <div class="info-card"><div class="info-label">تعداد اشخاص</div><div class="info-value" style="color:#7c3aed">${sorted.length}</div></div>
  </div>
  <div class="table-title">لیست کامل اشخاص و بیلانس</div>
  <table>
    <thead>
      <tr>
        <th class="center">#</th>
        <th class="center">کد</th>
        <th>نام</th>
        <th class="center">تلفن</th>
        <th>شهر/یادداشت</th>
        <th class="center">بیلانس (${reportAcc.currency})</th>
      </tr>
    </thead>
    <tbody>${rowsHtml}</tbody>
  </table>
  <div class="footer">
    <div class="footer-note">این گزارش به صورت سیستمی تولید شده و نیازی به امضا ندارد.</div>
    <div class="footer-sig">امضا و مهر مسئول مالی</div>
  </div>
</div></body></html>`);
      w.document.close();
      setTimeout(() => w.print(), 600);
      return;
    }

    const inItems  = reportData.filter(t => t.direction === "in");
    const outItems = reportData.filter(t => t.direction === "out");
    const totalIn  = inItems.reduce((s, t) => s + Math.abs(parseFloat(t.amount || 0)), 0);
    const totalOut = outItems.reduce((s, t) => s + Math.abs(parseFloat(t.amount || 0)), 0);
    const typeColor = TYPE_COLORS[reportAcc.type] || "#6366f1";

    // 🆕 ستون‌های گزارش بر اساس نوعیت حساب (همان منطق جدول نمایشی)
    const isIncome = reportAcc.type === "income";
    const isNonCashAsset = reportAcc.type === "non_cash_asset" && (reportAcc as any).auto_link !== "customers_receivable";
    const isExpenseAccount = reportAcc.type === "expense";
    const showExchange = !isIncome;
    type PCol = { key: string; label: string; center?: boolean };
    const pCols: PCol[] = [
      { key: "idx", label: "#", center: true },
      { key: "date", label: "تاریخ", center: true },
      { key: "serial", label: "شماره سند", center: true },
      { key: "type", label: "نوع معامله" },
      { key: "party", label: isExpenseAccount ? "گیرنده" : "طرف حساب / جزئیات" },
    ];
    if (isIncome) {
      pCols.push(
        { key: "notes", label: "توضیحات" },
        { key: "item_name", label: "جنس/خدمت" },
        { key: "quantity", label: "تعداد", center: true },
        { key: "unit", label: "واحد", center: true },
        { key: "unit_price", label: "قیمت واحد", center: true },
        { key: "warehouse", label: "گدام" },
        { key: "truck_plate", label: "شماره موتر", center: true },
        { key: "waybill_number", label: "شماره بارنامه", center: true },
      );
    } else if (isNonCashAsset) {
      pCols.push(
        { key: "category_name", label: "دسته‌بندی" },
        { key: "notes", label: "توضیحات" },
        { key: "item_name", label: "جنس/خدمت" },
        { key: "quantity", label: "تعداد", center: true },
        { key: "unit", label: "واحد", center: true },
        { key: "unit_price", label: "قیمت واحد", center: true },
        { key: "truck_plate", label: "شماره موتر", center: true },
        { key: "waybill_number", label: "شماره بارنامه", center: true },
      );
    } else if (isExpenseAccount) {
      pCols.push(
        { key: "category_name", label: "دسته‌بندی" },
        { key: "notes", label: "توضیحات" },
      );
    } else {
      pCols.push({ key: "notes", label: "توضیحات" });
    }
    pCols.push({ key: "currency", label: "ارز", center: true }, { key: "amount", label: "مبلغ", center: true });
    if (showExchange) pCols.push({ key: "exchange", label: "مبلغ اصلی × نرخ", center: true }, { key: "usd", label: "معادل USD", center: true });
    pCols.push({ key: "balance", label: "بیلانس", center: true });
    const pIdxCurrency = pCols.findIndex(c => c.key === "currency");
    const pIdxAmount = pCols.findIndex(c => c.key === "amount");
    const pMidSpan = pIdxCurrency - 3;
    const pTailSpan = pCols.length - pIdxAmount - 2; // ستون‌های بین «مبلغ» و «بیلانس» (نرخ/USD در صورت وجود)
    const headerHtml = pCols.map(c => `<th${c.center ? ' class="center"' : ""}>${c.label}</th>`).join("");

    // جمع به تفکیک ارز
    const currencyTotals: Record<string, { in: number; out: number }> = {};
    reportData.forEach(t => {
      const cur = t.currency || reportAcc.currency;
      if (!currencyTotals[cur]) currencyTotals[cur] = { in: 0, out: 0 };
      const amt = Math.abs(parseFloat(t.amount || 0));
      if (t.direction === "in") currencyTotals[cur].in += amt;
      else currencyTotals[cur].out += amt;
    });
    const currencySummaryHTML = Object.entries(currencyTotals).map(([cur, { in: i, out: o }]) => `
      <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:10px 14px;min-width:140px;flex:1">
        <div style="font-size:10px;color:#64748b;font-weight:600;margin-bottom:4px">${cur}</div>
        <div style="font-size:11px;color:#059669;font-weight:700">▲ ${new Intl.NumberFormat("en-US",{minimumFractionDigits:2}).format(i)}</div>
        <div style="font-size:11px;color:#dc2626;font-weight:700">▼ ${new Intl.NumberFormat("en-US",{minimumFractionDigits:2}).format(o)}</div>
        <div style="font-size:11px;color:#1d4ed8;font-weight:800;border-top:1px solid #e2e8f0;margin-top:4px;padding-top:4px">= ${new Intl.NumberFormat("en-US",{minimumFractionDigits:2}).format(i - o)}</div>
      </div>`).join("");

    // بیلانس تجمعی — شروع از بیلانس اولیه
    // اگر حساب در جدول جدید بیلانس اولیه ثبت‌شده دارد، از همان استفاده کن (دقیق و قابل اعتماد)
    // در غیر این صورت (حساب قدیمی)، رفتار قبلی حفظ می‌شود (محاسبه از روی balance فعلی)
    const openingBal = Number((reportAcc as any).period_opening_balance ?? reportAcc.opening_balance ?? 0);
    let runBal = openingBal;

    const fmtN = (n: number) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(n));

    const openingRow = `
      <tr style="background:#f0fdf4;border-bottom:2px solid #86efac">
        <td class="center" style="color:#94a3b8;font-size:10px">—</td>
        <td class="center" style="color:#059669;font-weight:700">${reportAcc.created_at ? gregorianToJalaliStr(reportAcc.created_at.split(" ")[0]) : "—"}</td>
        <td class="center" style="color:#059669;font-weight:700">—</td>
        <td colspan="${pMidSpan}" style="font-weight:800;color:#059669;font-size:12px">🏦 ${reportDateFrom ? "بیلانس منتقل شده قبل از بازه انتخابی" : "بیلانس اولیه حساب"}</td>
        <td class="center">${reportAcc.currency}</td>
        <td class="center" style="font-weight:800;font-size:13px;color:#059669">+${fmtN(openingBal)}</td>
        ${pTailSpan > 0 ? `<td class="center" colspan="${pTailSpan}">—</td>` : ""}
        <td class="center" style="font-weight:800;font-size:13px;color:#059669">${fmtN(openingBal)}</td>
      </tr>`;

    const rows = (openingRow) + (reportData.length === 0
      ? `<tr><td colspan="${pCols.length}" style="text-align:center;padding:40px;color:#94a3b8;font-size:13px">هیچ تراکنشی ثبت نشده است</td></tr>`
      : reportData.map((txn: any, i: number) => {
          const amt = Math.abs(parseFloat(txn.amount || 0));
          if (txn.direction === "in") runBal += amt; else runBal -= amt;
          const isForeign = txn.orig_currency && txn.orig_currency !== txn.currency && txn.orig_currency !== "USD";
          const typeIcon = txn.direction === "in" ? "▲" : "▼";
          const typeColors: Record<string, string> = {
            "دریافت پول": "#3b82f6", "پرداخت پول": "#f97316",
            "مصارف": "#ef4444", "COGS / هزینه اجناس": "#8b5cf6",
            "انتقال به حساب": "#06b6d4", "انتقال از حساب": "#06b6d4",
            "فروش جنس": "#10b981", "فروش خدمت": "#10b981",
            "خرید جنس": "#f59e0b", "خرید خدمت": "#f59e0b",
          };
          const tColor = typeColors[txn.type] || "#64748b";
          const foreignCell = isForeign ? fmtN(txn.orig_amount) + " " + txn.orig_currency + " × " + parseFloat(txn.exchange_rate || 1).toFixed(2) : "—";
          const amtColor = txn.direction === "in" ? "#059669" : "#dc2626";
          const balColor = runBal >= 0 ? "#059669" : "#dc2626";
          const pCell = (key: string) => {
            switch (key) {
              case "idx": return String(i + 1);
              case "date": return gregorianToJalaliStr(txn.date);
              case "serial": return txn.serial || "—";
              case "type": return `<span style="background:${tColor}18;color:${tColor};padding:2px 8px;border-radius:6px;font-size:10px;font-weight:700;white-space:nowrap">${typeIcon} ${txn.type}</span>`;
              case "party": return txn.party || "—";
              case "item_name": return txn.item_name || "—";
              case "quantity": return txn.quantity || "—";
              case "unit": return txn.unit || "—";
              case "unit_price": return txn.unit_price ? fmtN(txn.unit_price) : "—";
              case "warehouse": return txn.warehouse || "—";
              case "category_name": return txn.category_name || "—";
              case "truck_plate": return txn.truck_plate || "—";
              case "waybill_number": return txn.waybill_number || "—";
              case "currency": return txn.currency || reportAcc.currency;
              case "amount": return `<span style="font-weight:800;font-size:12px;color:${amtColor}">${txn.direction === "in" ? "+" : "−"}${fmtN(amt)}</span>`;
              case "exchange": return `<span style="font-size:10px;color:#64748b">${foreignCell}</span>`;
              case "usd": return (txn.customer_amount && txn.customer_currency === "USD" && txn.currency !== "USD") ? `$${fmtN(txn.customer_amount)}` : "—";
              case "notes": return txn.notes || "—";
              case "balance": return `<span style="font-weight:800;font-size:12px;color:${balColor}">${fmtN(runBal)}</span>`;
              default: return "—";
            }
          };
          const tds = pCols.map(c => {
            const cls = c.center ? ' class="center"' : "";
            const style = c.key === "party" ? ' style="max-width:180px"' : c.key === "notes" ? ' style="max-width:160px;font-size:10px;color:#475569"' : "";
            return `<td${cls}${style}>${pCell(c.key)}</td>`;
          }).join("");
          return `<tr class="${i % 2 === 0 ? "row-even" : "row-odd"}">${tds}</tr>`;
        }).join(""));

    w.document.write(`<!DOCTYPE html>
<html dir="rtl" lang="fa">
<head>
  <meta charset="UTF-8">
  <title>گزارش حساب — ${reportAcc.name}</title>
  <link href="https://fonts.googleapis.com/css2?family=Vazirmatn:wght@300;400;600;700;800&display=swap" rel="stylesheet">
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    @page { size: A4 landscape; margin: 10mm 12mm; }
    body { font-family: 'Vazirmatn', Tahoma, sans-serif; direction: rtl; font-size: 11px; color: #1e293b; background: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .page { padding: 6px; }

    /* هدر — بنر رنگی تمام‌عرض به سبک letterhead */
    .header { position: relative; display: flex; justify-content: space-between; align-items: center;
      padding: 18px 22px; margin-bottom: 18px; border-radius: 14px;
      background: linear-gradient(120deg, ${typeColor} 0%, ${typeColor}cc 55%, ${typeColor}99 100%);
      box-shadow: 0 6px 18px ${typeColor}33; overflow: hidden; }
    .header::after { content: ""; position: absolute; inset: 0; background: radial-gradient(circle at 85% -20%, rgba(255,255,255,0.22), transparent 55%); }
    .header-right { position: relative; z-index: 1; display: flex; align-items: center; gap: 14px; }
    .logo { width: 48px; height: 48px; background: rgba(255,255,255,0.18); border: 1px solid rgba(255,255,255,0.35); border-radius: 13px; display: flex; align-items: center; justify-content: center; font-size: 23px; backdrop-filter: blur(2px); }
    .header-title { font-size: 21px; font-weight: 800; color: #fff; letter-spacing: 0.2px; }
    .header-sub { font-size: 11px; color: rgba(255,255,255,0.85); margin-top: 3px; }
    .header-left { position: relative; z-index: 1; text-align: left; }
    .badge { display: inline-block; background: rgba(255,255,255,0.95); color: ${typeColor}; font-size: 10px; font-weight: 800; padding: 4px 14px; border-radius: 20px; margin-bottom: 5px; box-shadow: 0 2px 6px rgba(0,0,0,0.12); }
    .print-date { font-size: 10px; color: rgba(255,255,255,0.8); }

    /* کارت‌های اطلاعات */
    .info-row { display: flex; gap: 10px; margin-bottom: 14px; flex-wrap: wrap; padding: 0 2px; }
    .info-card { flex: 1; min-width: 100px; background: #fff; border: 1px solid #e7eaf0; border-radius: 12px; padding: 10px 13px; box-shadow: 0 2px 6px rgba(15,23,42,0.05); }
    .info-card.accent { border-color: ${typeColor}40; background: linear-gradient(180deg, ${typeColor}0d, #fff 70%); }
    .info-label { font-size: 9px; color: #94a3b8; font-weight: 700; margin-bottom: 4px; letter-spacing: 0.5px; }
    .info-value { font-size: 13.5px; font-weight: 800; color: #0f172a; }
    .info-card.balance .info-value { color: ${(reportAcc.balance || 0) >= 0 ? "#059669" : "#dc2626"}; }

    /* خلاصه ارزی */
    .currency-section { margin-bottom: 14px; padding: 0 2px; }
    .currency-title { font-size: 11px; font-weight: 800; color: #374151; margin-bottom: 7px; padding-right: 9px; border-right: 3px solid ${typeColor}; }
    .currency-row { display: flex; gap: 8px; flex-wrap: wrap; }

    /* خلاصه کلی */
    .summary-row { display: flex; gap: 10px; margin-bottom: 18px; padding: 0 2px; }
    .sum-box { flex: 1; border-radius: 12px; padding: 10px 13px; box-shadow: 0 2px 6px rgba(15,23,42,0.05); position: relative; }
    .sum-box::before { content: ""; position: absolute; top: 10px; bottom: 10px; right: 0; width: 4px; border-radius: 3px; }
    .sum-box.in  { background: linear-gradient(180deg,#f0fdf4,#ffffff 85%); border: 1px solid #bbf7d0; }
    .sum-box.in::before  { background: #22c55e; }
    .sum-box.out { background: linear-gradient(180deg,#fff1f2,#ffffff 85%); border: 1px solid #fecaca; }
    .sum-box.out::before { background: #ef4444; }
    .sum-box.net { background: linear-gradient(180deg,#eff6ff,#ffffff 85%); border: 1px solid #bfdbfe; }
    .sum-box.net::before { background: #3b82f6; }
    .sum-box.cnt { background: linear-gradient(180deg,#faf5ff,#ffffff 85%); border: 1px solid #e9d5ff; }
    .sum-box.cnt::before { background: #a855f7; }
    .sum-label { font-size: 9px; font-weight: 700; color: #64748b; margin-bottom: 4px; }
    .sum-value { font-size: 14px; font-weight: 800; }
    .sum-box.in  .sum-value { color: #059669; }
    .sum-box.out .sum-value { color: #dc2626; }
    .sum-box.net .sum-value { color: #1d4ed8; }
    .sum-box.cnt .sum-value { color: #7c3aed; }

    /* جدول */
    .table-title { font-size: 12px; font-weight: 800; color: #374151; margin-bottom: 9px; padding-right: 9px; border-right: 3px solid ${typeColor}; }
    .table-wrap { border-radius: 12px; overflow: hidden; box-shadow: 0 4px 14px rgba(15,23,42,0.08); border: 1px solid #e7eaf0; }
    table { width: 100%; border-collapse: collapse; font-size: 10.5px; }
    thead tr { background: linear-gradient(120deg, #1e293b, #334155 60%, ${typeColor}cc); }
    th { padding: 10px 8px; text-align: right; font-weight: 700; color: #f1f5f9; white-space: nowrap; font-size: 10px; letter-spacing: 0.2px; }
    th.center { text-align: center; }
    .row-even { background: #ffffff; }
    .row-odd  { background: #f8fafc; }
    tbody tr:hover { background: ${typeColor}0a; }
    td { padding: 7.5px 8px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
    td.center { text-align: center; }
    tr:last-child td { border-bottom: none; }

    /* فوتر */
    .footer { margin-top: 22px; padding-top: 14px; border-top: 1px dashed #d8dee8; display: flex; justify-content: space-between; align-items: flex-end; }
    .footer-note { font-size: 9.5px; color: #94a3b8; max-width: 60%; }
    .footer-sig { font-size: 10px; color: #64748b; border-top: 1px solid #cbd5e1; padding-top: 6px; min-width: 170px; text-align: center; }

    @media print { body { font-size: 10px; } thead { display: table-header-group; } tr { page-break-inside: avoid; } .header, .info-card, .sum-box, .table-wrap { box-shadow: none !important; } }
  </style>
</head>
<body><div class="page">

  <!-- هدر -->
  <div class="header">
    <div class="header-right">
      <div class="logo">🏦</div>
      <div>
        <div class="header-title">${reportAcc.name}</div>
        <div class="header-sub">گزارش کامل تراکنش‌های حساب</div>
      </div>
    </div>
    <div class="header-left">
      <div><span class="badge">${TYPES[reportAcc.type] || reportAcc.type}</span></div>
      <div class="print-date">تاریخ چاپ: ${new Date().toLocaleDateString("fa-IR")} — ${new Date().toLocaleTimeString("fa-IR")}</div>
      ${reportDateFrom || reportDateTo ? `<div class="print-date" style="color:#8b5cf6;font-weight:600;margin-top:2px">📅 ${reportDateFrom ? gregorianToJalaliStr(reportDateFrom) : "ابتدا"} تا ${reportDateTo ? gregorianToJalaliStr(reportDateTo) : "انتها"}</div>` : ""}
    </div>
  </div>

  <!-- اطلاعات حساب -->
  <div class="info-row">
    <div class="info-card accent"><div class="info-label">کد حساب</div><div class="info-value">${reportAcc.code}</div></div>
    <div class="info-card"><div class="info-label">نوعیت</div><div class="info-value">${TYPES[reportAcc.type] || reportAcc.type}</div></div>
    <div class="info-card"><div class="info-label">ارز پایه</div><div class="info-value">${reportAcc.currency}</div></div>
    <div class="info-card balance accent"><div class="info-label">موجودی فعلی</div><div class="info-value">${fmt(reportAcc.balance, reportAcc.currency)}</div></div>
    <div class="info-card"><div class="info-label">تعداد تراکنش</div><div class="info-value" style="color:#7c3aed">${reportData.length}</div></div>
  </div>

  <!-- خلاصه کلی -->
  <div class="summary-row">
    <div class="sum-box in"><div class="sum-label">▲ جمع ورودی‌ها</div><div class="sum-value">+${fmt(totalIn, reportAcc.currency)}</div></div>
    <div class="sum-box out"><div class="sum-label">▼ جمع خروجی‌ها</div><div class="sum-value">−${fmt(totalOut, reportAcc.currency)}</div></div>
    <div class="sum-box net"><div class="sum-label">خالص گردش</div><div class="sum-value">${fmt(totalIn - totalOut, reportAcc.currency)}</div></div>
    <div class="sum-box cnt"><div class="sum-label">تراکنش‌ها</div><div class="sum-value">${reportData.length}</div></div>
  </div>

  <!-- خلاصه به تفکیک ارز -->
  ${Object.keys(currencyTotals).length > 1 ? `
  <div class="currency-section">
    <div class="currency-title">جمع به تفکیک ارز</div>
    <div class="currency-row">${currencySummaryHTML}</div>
  </div>` : ""}

  <!-- جدول -->
  <div class="table-title">جزئیات کامل تراکنش‌ها</div>
  <div class="table-wrap">
    <table>
      <thead>
        <tr>${headerHtml}</tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
  </div>

  <!-- فوتر -->
  <div class="footer">
    <div class="footer-note">این گزارش به صورت سیستمی تولید شده و نیازی به امضا ندارد.</div>
    <div class="footer-sig">امضا و مهر مسئول مالی</div>
  </div>

</div></body></html>`);

    w.document.close();
    setTimeout(() => w.print(), 600);
  };

  // استایل‌های مشترک
  const card = {
    borderRadius: 14, padding: "14px 16px",
    background: dark ? "rgba(255,255,255,0.03)" : "#fff",
    border: `1px solid ${T.cardBorder}`,
  };

  const inp = (val: string, onChange: (v: string) => void, ph?: string) => (
    <input value={val} onChange={e => onChange(e.target.value)} placeholder={ph}
      style={{
        width: "100%", padding: "10px 14px", borderRadius: 10,
        border: `1.5px solid ${T.border}`, background: T.input,
        color: T.text, fontSize: 13, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
        boxSizing: "border-box", outline: "none",
      }} />
  );

  return (
    <div style={{ fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>

      {/* Toast */}
      {toast.text && (
        <div style={{
          position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)",
          background: toast.ok ? "#10b981" : "#ef4444", color: "#fff",
          padding: "12px 28px", borderRadius: 12, fontSize: 13, fontWeight: 700,
          zIndex: 9999, boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
        }}>{toast.text}</div>
      )}

      {/* ───────────────────────── هدر صفحه ───────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 20, flexWrap: "wrap", gap: 14 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 21, fontWeight: 800, color: T.text, letterSpacing: "-0.01em" }}>حساب‌ها</h1>
          <p style={{ margin: "5px 0 0", fontSize: 12.5, color: T.subtext }}>مدیریت دفتر حساب‌ها و دسترسی سریع به گزارش هر حساب</p>
        </div>
        {canAdd && (
          <button onClick={() => setShowAdd(true)} style={{
            display: "flex", alignItems: "center", gap: 8, padding: "11px 22px",
            borderRadius: 12, border: "none", cursor: "pointer",
            background: ACCENT_GRAD, color: "#fff",
            fontSize: 13.5, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
            boxShadow: `0 10px 22px ${ACCENT}3d`,
          }}>
            <Plus size={16} /> ایجاد حساب جدید
          </button>
        )}
      </div>

      {/* ───────────────────────── نوار جستجو / فیلتر ───────────────────────── */}
      <div style={{
        display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap", alignItems: "center",
        background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 16, padding: 10,
        boxShadow: dark ? "0 1px 2px rgba(0,0,0,0.25)" : "0 1px 2px rgba(15,23,42,0.04)",
      }}>
        <div style={{ position: "relative", flex: 1, minWidth: 200 }} onBlur={() => setTimeout(() => setShowSuggest(false), 120)}>
          <Search size={15} style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", color: T.subtext, zIndex: 1 }} />
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setShowSuggest(true); }}
            onFocus={() => setShowSuggest(true)}
            placeholder="جستجوی نام یا کد حساب..."
            style={{
              width: "100%", padding: "11px 40px 11px 16px", borderRadius: 11,
              border: `1.5px solid ${search && showSuggest ? ACCENT : "transparent"}`,
              background: dark ? "rgba(255,255,255,0.04)" : "#f8fafc",
              color: T.text, fontSize: 13.5, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
              boxSizing: "border-box", outline: "none", transition: "border-color 0.15s, background 0.15s",
            }} />
          {search && showSuggest && searchSuggestions.length > 0 && (
            <div style={{
              position: "absolute", top: "calc(100% + 8px)", right: 0, left: 0, zIndex: 50,
              background: dark ? "#1b2333" : "#fff", borderRadius: 14,
              border: `1px solid ${BORDER}`,
              boxShadow: dark ? "0 16px 36px rgba(0,0,0,0.5)" : "0 16px 36px rgba(15,23,42,0.14)",
              overflow: "hidden", maxHeight: 320, overflowY: "auto",
            }}>
              {searchSuggestions.map(acc => (
                <div key={acc.id}
                  onClick={() => { setSearch(acc.name); setShowSuggest(false); handleReport(acc); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "11px 14px",
                    cursor: "pointer", borderBottom: `1px solid ${BORDER}`,
                    transition: "background 0.12s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = dark ? "rgba(255,255,255,0.05)" : "#f8fafc"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  <TypeBadge type={acc.type} />
                  <span style={{ fontWeight: 700, fontSize: 13, color: T.text, flex: 1 }}>{acc.name}</span>
                  <span style={{ fontSize: 11, color: T.subtext, fontFamily: "monospace" }}>{acc.code}</span>
                  <span style={{ fontWeight: 800, fontSize: 12, fontVariantNumeric: "tabular-nums", color: acc.balance >= 0 ? POS : NEG }}>{fmt(acc.balance, acc.currency)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <SearchableSelect
          options={[{ value: "", label: "همه نوعیت‌ها" }, ...Object.entries(TYPES).map(([v, l]) => ({ value: v, label: l }))]}
          value={typeFilter} onChange={setTypeFilter}
          style={{ minWidth: 160 }}
        />
      </div>

      {/* ───────────────────────── نوار صفحه‌بندی — بالای جدول، هر صفحه ۵۰ حساب ───────────────────────── */}
      {!loading && filtered.length > 0 && (
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          marginBottom: 12, flexWrap: "wrap", gap: 10, padding: "10px 16px",
          background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 14,
        }}>
          <span style={{ fontSize: 12, color: T.subtext }}>
            نمایش <b style={{ color: T.text }}>{pagedGroups.reduce((s, g) => s + 1 + g.kids.length, 0)}</b> ردیف از مجموع <b style={{ color: T.text }}>{filtered.length}</b> حساب — هر صفحه ۵۰ حساب (مرتب‌شده بر اساس شماره حساب)
          </span>
          {totalPages > 1 && (
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <button onClick={() => setPage(1)} disabled={safePage === 1} title="صفحه اول" style={pagerBtnStyle(safePage === 1)}>
                <ChevronsRight size={14} />
              </button>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1} title="صفحه قبلی" style={pagerBtnStyle(safePage === 1)}>
                <ChevronRight size={14} />
              </button>
              <span style={{ fontSize: 12.5, color: T.text, fontWeight: 700, minWidth: 84, textAlign: "center" }}>
                صفحه {safePage} از {totalPages}
              </span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} title="صفحه بعدی" style={pagerBtnStyle(safePage === totalPages)}>
                <ChevronLeft size={14} />
              </button>
              <button onClick={() => setPage(totalPages)} disabled={safePage === totalPages} title="صفحه آخر" style={pagerBtnStyle(safePage === totalPages)}>
                <ChevronsLeft size={14} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* ───────────────────────── جدول حساب‌ها ───────────────────────── */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: T.subtext }}>در حال بارگذاری...</div>
      ) : filtered.length === 0 ? (
        <div style={{ ...card, textAlign: "center", padding: 48, color: T.subtext }}>
          هیچ حسابی یافت نشد
        </div>
      ) : (
        <div style={{
          borderRadius: 16, overflow: "hidden", border: `1px solid ${BORDER}`, background: SURFACE,
          boxShadow: dark ? "0 1px 2px rgba(0,0,0,0.25)" : "0 1px 2px rgba(15,23,42,0.05)",
        }}>
          {/* ردیف هدر ستون‌ها */}
          <div style={{
            display: "grid", gridTemplateColumns: "30px 150px 1fr 120px 160px 150px",
            alignItems: "center", gap: 8, padding: "11px 18px",
            background: dark ? "rgba(255,255,255,0.035)" : "#f8fafc",
            borderBottom: `1px solid ${BORDER}`,
          }}>
            <span />
            <span style={colHeadStyle}>نوعیت</span>
            <span style={colHeadStyle}>نام حساب</span>
            <span style={{ ...colHeadStyle, textAlign: "center" }}>کد</span>
            <span style={{ ...colHeadStyle, textAlign: "left" }}>بیلانس</span>
            <span style={{ ...colHeadStyle, textAlign: "left" }}>عملیات</span>
          </div>

          {pagedGroups.map(({ acc, kids }, gi) => {
            const isOpen    = expanded.has(acc.id);
            const hasKids   = children(acc.id).length > 0; // صرف‌نظر از باز/بسته بودن، برای نمایش فلش
            const typeColor = TYPE_COLORS[acc.type] || "#6b7280";
            const TypeIcon  = TYPE_ICONS[acc.type] || Wallet;

            return (
              <div key={acc.id} style={{ borderTop: gi === 0 ? "none" : `1px solid ${BORDER}` }}>
                {/* ردیف مادر — نوار رنگی سمت راست و افکت هاور هم‌رنگ با نوعیت حساب، برای تشخیص سریع با چشم */}
                <div
                  onMouseEnter={e => { e.currentTarget.style.background = `${typeColor}0c`; e.currentTarget.style.boxShadow = `inset 0 0 0 1px ${typeColor}22`; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.boxShadow = "none"; }}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "30px 150px 1fr 120px 160px 150px",
                    alignItems: "center", gap: 8, padding: "13px 18px",
                    opacity: acc.is_active ? 1 : 0.5, transition: "background 0.15s, box-shadow 0.15s",
                    borderRight: `3px solid ${typeColor}70`,
                  }}>
                  {/* expand */}
                  <button onClick={() => hasKids && toggleExpand(acc.id)}
                    style={{ background: "none", border: "none", cursor: hasKids ? "pointer" : "default", color: T.subtext, padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {hasKids ? (isOpen ? <ChevronDown size={15} /> : <ChevronLeft size={15} />) : null}
                  </button>

                  <TypeBadge type={acc.type} />

                  {/* نام + نشان دایره‌ای رنگی/آیکون‌دار همان نوعیت حساب */}
                  <span style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 700, color: T.text, fontSize: 14 }}>
                    <TypeAvatar type={acc.type} />
                    {acc.name}
                  </span>

                  {/* کد */}
                  <span style={{
                    fontSize: 11.5, color: T.subtext, fontFamily: "'SF Mono',Consolas,monospace",
                    textAlign: "center", background: dark ? "rgba(255,255,255,0.05)" : "#f1f5f9",
                    borderRadius: 6, padding: "3px 0",
                  }}>{acc.code}</span>

                  {/* بیلانس */}
                  <span style={{
                    fontWeight: 800, fontSize: 13.5, textAlign: "left", fontVariantNumeric: "tabular-nums",
                    color: acc.balance >= 0 ? POS : NEG,
                  }}>{fmt(acc.balance, acc.currency)}</span>

                  {/* دکمه‌ها */}
                  <div style={{ display: "flex", gap: 5, justifyContent: "flex-end" }}>
                    {canEdit && (
                      <button title="ویرایش" onClick={() => openEditModal(acc)}
                        style={iconBtnStyle("#3b82f6")}><Edit2 size={13} /></button>
                    )}
                    {canEdit && (
                      <button title={acc.is_active ? "مخفی کردن" : "نمایش"} onClick={() => handleToggle(acc)}
                        style={iconBtnStyle(acc.is_active ? "#f59e0b" : "#10b981")}>
                        {acc.is_active ? <EyeOff size={13} /> : <Eye size={13} />}
                      </button>
                    )}
                    <button title="گزارش" onClick={() => handleReport(acc)} style={iconBtnStyle(ACCENT)}><FileText size={13} /></button>
                    {canDelete && (
                      <button title="حذف" onClick={() => handleDelete(acc)} style={iconBtnStyle("#ef4444")}><Trash2 size={13} /></button>
                    )}
                  </div>
                </div>

                {/* زیرمجموعه‌ها */}
                {isOpen && kids.map(child => (
                  <div key={child.id}
                    onMouseEnter={e => { e.currentTarget.style.background = `${typeColor}09`; }}
                    onMouseLeave={e => { e.currentTarget.style.background = dark ? "rgba(255,255,255,0.012)" : "#fcfcfd"; }}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "30px 150px 1fr 120px 160px 150px",
                      alignItems: "center", gap: 8,
                      padding: "10px 18px 10px 18px",
                      background: dark ? "rgba(255,255,255,0.012)" : "#fcfcfd",
                      borderTop: `1px solid ${BORDER}`,
                      borderRight: `3px solid ${typeColor}30`,
                      opacity: child.is_active ? 1 : 0.5, transition: "background 0.15s",
                    }}>
                    {/* اتصال سلسله‌مراتبی — همان آیکون نوعیت، کوچک‌تر و کم‌رنگ‌تر */}
                    <span style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <TypeIcon size={13} color={typeColor} strokeWidth={2.4} />
                    </span>
                    <span style={{ fontSize: 10.5, color: T.subtext, textAlign: "center" }}>زیرمجموعه</span>
                    {/* نام */}
                    <span style={{ fontWeight: 600, color: T.text, fontSize: 13, paddingRight: 14 }}>↳ {child.name}</span>
                    {/* کد */}
                    <span style={{ fontSize: 11, color: T.subtext, fontFamily: "'SF Mono',Consolas,monospace", textAlign: "center" }}>{child.code}</span>
                    {/* بیلانس */}
                    <span style={{ fontWeight: 700, fontSize: 13, fontVariantNumeric: "tabular-nums", color: child.balance >= 0 ? POS : NEG, textAlign: "left" }}>
                      {fmt(child.balance, child.currency)}
                    </span>
                    {/* دکمه‌ها */}
                    <div style={{ display: "flex", gap: 5, justifyContent: "flex-end" }}>
                      {canEdit && (
                        <button title="ویرایش" onClick={() => openEditModal(child)}
                          style={iconBtnStyle("#3b82f6")}><Edit2 size={13} /></button>
                      )}
                      {canEdit && (
                        <button title={child.is_active ? "مخفی کردن" : "نمایش"} onClick={() => handleToggle(child)}
                          style={iconBtnStyle(child.is_active ? "#f59e0b" : "#10b981")}>
                          {child.is_active ? <EyeOff size={13} /> : <Eye size={13} />}
                        </button>
                      )}
                      <button title="گزارش" onClick={() => handleReport(child)} style={iconBtnStyle(ACCENT)}><FileText size={13} /></button>
                      {canDelete && (
                        <button title="حذف" onClick={() => handleDelete(child)} style={iconBtnStyle("#ef4444")}><Trash2 size={13} /></button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal: ایجاد حساب */}
      {showAdd && (
        <Modal title="ایجاد حساب جدید" onClose={() => setShowAdd(false)} dark={dark} T={T}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div style={{ gridColumn: "1/-1" }}>
              <Label T={T}>نام حساب *</Label>
              {inp(form.name, v => setForm(f => ({ ...f, name: v })), "نام حساب را وارد کنید")}
            </div>
            <div>
              <Label T={T}>نوعیت حساب *</Label>
              <SearchableSelect
                options={Object.entries(TYPES).map(([v, l]) => ({ value: v, label: l }))}
                value={form.type}
                onChange={v => setForm(f => ({ ...f, type: v, parent_id: "" }))}
                placeholder="انتخاب نوعیت"
              />
            </div>
            <div>
              <Label T={T}>ارز *</Label>
              <SearchableSelect
                options={CURRENCIES.map(c => ({ value: c, label: c }))}
                value={form.currency}
                onChange={v => setForm(f => ({ ...f, currency: v, parent_id: "" }))}
                placeholder="انتخاب ارز"
              />
            </div>
            <div style={{ gridColumn: "1/-1" }}>
              <Label T={T}>حساب مادر (اختیاری)</Label>
              <SearchableSelect
                options={[{ value: "", label: "— حساب اصلی (بدون مادر) —" }, ...parentOptions]}
                value={form.parent_id}
                onChange={v => setForm(f => ({ ...f, parent_id: v }))}
                placeholder="انتخاب حساب مادر"
              />
            </div>
            <div>
              <Label T={T}>بیلانس اولیه</Label>
              {inp(form.opening_balance, v => setForm(f => ({ ...f, opening_balance: v })), "0.00")}
            </div>
            <div>
              <Label T={T}>تاریخ ثبت</Label>
              <PersianDatePicker value={form.created_date} onChange={v => setForm(f => ({ ...f, created_date: v }))} />
            </div>
            <div style={{ gridColumn: "1/-1" }}>
              <Label T={T}>توضیحات</Label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                rows={2} placeholder="توضیحات اختیاری..."
                style={{
                  width: "100%", padding: "10px 14px", borderRadius: 10,
                  border: `1.5px solid ${T.border}`, background: T.input,
                  color: T.text, fontSize: 13, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
                  resize: "vertical", boxSizing: "border-box", outline: "none",
                }} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
            <BtnCancel onClick={() => setShowAdd(false)} T={T} />
            <BtnSave onClick={() => submit(handleAdd)} loading={submitting} color="#10b981" label="ایجاد حساب" />
          </div>
        </Modal>
      )}

      {/* Modal: ویرایش */}
      {editAcc && (
        <Modal title={`ویرایش: ${editAcc.name}`} onClose={() => setEditAcc(null)} dark={dark} T={T}>
          {/* هشدار برای حساب‌های قدیمی که هنوز بیلانس اولیه ثبت‌شده ندارند */}
          {editAcc.opening_balance == null && (
            <div style={{
              marginBottom: 14, padding: "10px 14px", borderRadius: 10,
              background: dark ? "rgba(245,158,11,0.1)" : "#fffbeb",
              border: `1.5px solid ${dark ? "rgba(245,158,11,0.3)" : "#fde68a"}`,
              color: dark ? "#fbbf24" : "#92400e", fontSize: 12, lineHeight: 1.7,
            }}>
              ⚠️ این یک حساب قدیمی است و هنوز بیلانس اولیه ثبت‌شده ندارد. اگر می‌خواهید
              بیلانس اولیه را ثبت کنید، چک‌باکس پایین را فعال کنید. در غیر این صورت،
              این فیلد دست نمی‌خورد و گزارش حساب مثل قبل کار می‌کند.
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div style={{ gridColumn: "1/-1" }}>
              <Label T={T}>نام حساب *</Label>
              {inp(editForm.name, v => setEditForm(f => ({ ...f, name: v })))}
            </div>

            <div>
              <Label T={T}>نوعیت حساب *</Label>
              <SearchableSelect
                options={Object.entries(TYPES).map(([v, l]) => ({ value: v, label: l }))}
                value={editForm.type}
                onChange={v => setEditForm(f => ({ ...f, type: v, parent_id: "" }))}
                placeholder="انتخاب نوعیت"
              />
            </div>

            <div>
              <Label T={T}>ارز *</Label>
              <SearchableSelect
                options={CURRENCIES.map(c => ({ value: c, label: c }))}
                value={editForm.currency}
                onChange={v => setEditForm(f => ({ ...f, currency: v, parent_id: "" }))}
                placeholder="انتخاب ارز"
              />
            </div>

            <div style={{ gridColumn: "1/-1" }}>
              <Label T={T}>حساب مادر (اختیاری)</Label>
              <SearchableSelect
                options={[{ value: "", label: "— حساب اصلی (بدون مادر) —" }, ...editParentOptions]}
                value={editForm.parent_id}
                onChange={v => setEditForm(f => ({ ...f, parent_id: v }))}
                placeholder="انتخاب حساب مادر"
              />
            </div>

            <div style={{ gridColumn: "1/-1" }}>
              <Label T={T}>توضیحات</Label>
              <textarea value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                rows={2} style={{
                  width: "100%", padding: "10px 14px", borderRadius: 10,
                  border: `1.5px solid ${T.border}`, background: T.input,
                  color: T.text, fontSize: 13, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
                  resize: "vertical", boxSizing: "border-box", outline: "none",
                }} />
            </div>
          </div>

          {/* 🆕 بخش جداگانه برای ویرایش بیلانس اولیه — فقط اگر چک‌باکس فعال باشد */}
          <div style={{
            marginTop: 18, padding: "14px 16px", borderRadius: 12,
            background: editForm.change_opening_balance
              ? (dark ? "rgba(59,130,246,0.08)" : "#eff6ff")
              : (dark ? "rgba(255,255,255,0.02)" : "#f8fafc"),
            border: `1.5px solid ${editForm.change_opening_balance
              ? (dark ? "rgba(59,130,246,0.3)" : "#bfdbfe")
              : T.cardBorder}`,
            transition: "all 0.2s",
          }}>
            <label style={{
              display: "flex", alignItems: "center", gap: 10,
              cursor: "pointer", userSelect: "none",
            }}>
              <input
                type="checkbox"
                checked={editForm.change_opening_balance}
                onChange={e => setEditForm(f => ({ ...f, change_opening_balance: e.target.checked }))}
                style={{
                  width: 18, height: 18, cursor: "pointer",
                  accentColor: "#3b82f6",
                }}
              />
              <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>
                ✏️ می‌خواهم بیلانس اولیه را تغییر دهم
              </span>
            </label>

            {editForm.change_opening_balance && (
              <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <div>
                  <Label T={T}>بیلانس اولیه جدید</Label>
                  {inp(editForm.opening_balance, v => setEditForm(f => ({ ...f, opening_balance: v })), "0.00")}
                </div>
                <div style={{ gridColumn: "1/-1" }}>
                  <Label T={T}>توضیحات ویرایش (اختیاری)</Label>
                  {inp(editForm.adjustment_note || "", v => setEditForm(f => ({ ...f, adjustment_note: v })), "دلیل تغییر بیلانس اولیه...")}
                </div>
                <div>
                  <Label T={T}>تاریخ بیلانس اولیه</Label>
                  <PersianDatePicker value={editForm.opening_date} onChange={v => setEditForm(f => ({ ...f, opening_date: v }))} />
                </div>
                <div style={{ gridColumn: "1/-1" }}>
                  <div style={{
                    padding: "8px 12px", borderRadius: 8,
                    background: dark ? "rgba(245,158,11,0.08)" : "#fffbeb",
                    border: `1px solid ${dark ? "rgba(245,158,11,0.2)" : "#fde68a"}`,
                    fontSize: 11, color: dark ? "#fbbf24" : "#92400e", lineHeight: 1.7,
                  }}>
                    💡 با ذخیره، اختلاف بین بیلانس اولیه قدیم و جدید به موجودی فعلی <strong>اضافه یا کم</strong> می‌شود (نه دو برابر، نه کپی).
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* اطلاعات راهنما */}
          <div style={{
            marginTop: 14, padding: "10px 14px", borderRadius: 10,
            background: dark ? "rgba(59,130,246,0.08)" : "#eff6ff",
            border: `1px solid ${dark ? "rgba(59,130,246,0.2)" : "#dbeafe"}`,
            fontSize: 11, color: dark ? "#93c5fd" : "#1e40af", lineHeight: 1.7,
          }}>
            <div>📊 موجودی فعلی این حساب: <strong>{fmt(editAcc.balance, editAcc.currency)}</strong></div>
            {editAcc.opening_balance != null && (
              <div>🏦 بیلانس اولیه ثبت‌شده: <strong>{fmt(editAcc.opening_balance, editAcc.currency)}</strong></div>
            )}
          </div>

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
            <BtnCancel onClick={() => setEditAcc(null)} T={T} />
            <BtnSave onClick={() => submit(handleEdit)} loading={submitting} color="#3b82f6" label="ذخیره" />
          </div>
        </Modal>
      )}

      {/* Modal: گزارش */}
      {reportAcc && (
        <Modal title={`گزارش حساب: ${reportAcc.name} (${reportAcc.code})`} onClose={() => { setReportAcc(null); setReportDateFrom(""); setReportDateTo(""); }} dark={dark} T={T} wide>

          {/* ── فیلتر تاریخ ── */}
          <div style={{
            marginBottom: 16, padding: "14px 16px", borderRadius: 12,
            background: dark ? "rgba(139,92,246,0.08)" : "#f5f3ff",
            border: `1.5px solid ${dark ? "rgba(139,92,246,0.2)" : "#e9d5ff"}`,
          }}>
            {/* دکمه‌های سریع */}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12, alignItems: "center" }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#8b5cf6", marginLeft: 6 }}>🗓 دوره سریع:</span>
              {[
                { key: "today",  label: "امروز" },
                { key: "week",   label: "۷ روز اخیر" },
                { key: "month",  label: "این ماه" },
                { key: "3month", label: "۳ ماه اخیر" },
                { key: "6month", label: "۶ ماه اخیر" },
                { key: "year",   label: "امسال" },
              ].map(q => (
                <button key={q.key} onClick={() => setQuickDate(q.key)} style={{
                  padding: "5px 12px", borderRadius: 7, border: `1px solid ${dark ? "rgba(139,92,246,0.3)" : "#c4b5fd"}`,
                  background: "transparent", color: "#8b5cf6", cursor: "pointer",
                  fontSize: 11, fontWeight: 600, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
                }}>{q.label}</button>
              ))}
              {(reportDateFrom || reportDateTo) && (
                <button onClick={clearDateFilter} style={{
                  padding: "5px 12px", borderRadius: 7, border: `1px solid ${T.cardBorder}`,
                  background: "transparent", color: T.subtext, cursor: "pointer",
                  fontSize: 11, fontFamily: "'Vazirmatn','Segoe UI',sans-serif", marginRight: "auto",
                }}>✕ پاک کردن</button>
              )}
            </div>
            {/* از تاریخ — تا تاریخ */}
            <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: T.subtext, marginBottom: 4 }}>از تاریخ</div>
                <PersianDatePicker value={reportDateFrom} onChange={v => { setReportDateFrom(v); }} />
              </div>
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: T.subtext, marginBottom: 4 }}>تا تاریخ</div>
                <PersianDatePicker value={reportDateTo} onChange={v => { setReportDateTo(v); }} />
              </div>
              <button onClick={applyDateFilter} style={{
                padding: "9px 20px", borderRadius: 9, border: "none", cursor: "pointer",
                background: "linear-gradient(135deg,#8b5cf6,#7c3aed)", color: "#fff",
                fontSize: 13, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
                whiteSpace: "nowrap",
              }}>🔍 اعمال فیلتر</button>
            </div>
            {(reportDateFrom || reportDateTo) && (
              <div style={{ marginTop: 8, fontSize: 11, color: "#8b5cf6", fontWeight: 600 }}>
                📌 {reportDateFrom ? `از: ${gregorianToJalaliStr(reportDateFrom)}` : "ابتدا"} — {reportDateTo ? `تا: ${gregorianToJalaliStr(reportDateTo)}` : "انتها"}
                {" | "}{reportData.length} تراکنش
              </div>
            )}
          </div>

          <div id="report-content">
            {/* هدر گزارش */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <h2 style={{ margin: 0, fontSize: 16, color: dark ? "#fff" : "#1e293b" }}>
                گزارش حساب: {reportAcc.name}
              </h2>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                <span style={{ fontSize: 12, color: T.subtext }}>کد: <strong style={{ color: T.text }}>{reportAcc.code}</strong></span>
                <span style={{ fontSize: 12, color: T.subtext }}>نوعیت: <strong style={{ color: T.text }}>{TYPES[reportAcc.type]}</strong></span>
                <span style={{ fontSize: 12, color: T.subtext }}>ارز: <strong style={{ color: T.text }}>{reportAcc.currency}</strong></span>
                <span style={{ fontSize: 12, color: T.subtext }}>موجودی فعلی: <strong style={{ color: reportAcc.balance >= 0 ? "#10b981" : "#ef4444" }}>{fmt(reportAcc.balance, reportAcc.currency)}</strong></span>
                <span style={{ fontSize: 11, color: T.subtext }}>
                  {reportAutoLink ? "تعداد اشخاص" : "تعداد تراکنش"}: <strong style={{ color: T.text }}>{reportAutoLink ? reportEntities.length : reportData.length}</strong>
                </span>
              </div>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={handleExcelDownload} disabled={excelLoading} style={{
                  display: "flex", alignItems: "center", gap: 6, padding: "8px 18px",
                  borderRadius: 9, border: "none", cursor: excelLoading ? "not-allowed" : "pointer",
                  background: excelLoading ? "#9ca3af" : "linear-gradient(135deg,#059669,#047857)", color: "#fff",
                  fontSize: 12, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif", whiteSpace: "nowrap",
                }}>📊 {excelLoading ? "..." : "دانلود اکسل"}</button>
                <button onClick={printReport} style={{
                  display: "flex", alignItems: "center", gap: 6, padding: "8px 18px",
                  borderRadius: 9, border: "none", cursor: "pointer",
                  background: "linear-gradient(135deg,#8b5cf6,#7c3aed)", color: "#fff",
                  fontSize: 12, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif", whiteSpace: "nowrap",
                }}><Printer size={14} /> چاپ گزارش</button>
              </div>
            </div>

            {reportLoading ? (
              <div style={{ textAlign: "center", padding: 40, color: T.subtext }}>در حال بارگذاری...</div>
            ) : reportAutoLink ? (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, border: `1px solid ${T.cardBorder}` }}>
                  <thead>
                    <tr style={{ background: dark ? "#1e293b" : "#334155" }}>
                      {["#","کد","نام","تلفن","شهر/یادداشت","ارز","بیلانس"].map(h => (
                        <th key={h} style={{ padding: "10px 10px", textAlign: "right", fontWeight: 700, color: "#f1f5f9", whiteSpace: "nowrap", fontSize: 11 }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {reportEntities.length === 0 ? (
                      <tr><td colSpan={7} style={{ textAlign: "center", padding: 32, color: T.subtext }}>هیچ طلب‌داری با بیلانس غیر صفر یافت نشد</td></tr>
                    ) : reportEntities.map((en: any, i: number) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,0.02)" : "#f8fafc") }}>
                        <td style={{ padding: "8px 10px", color: T.subtext, fontSize: 11 }}>{i+1}</td>
                        <td style={{ padding: "8px 10px", color: "#8b5cf6", fontFamily: "monospace", fontSize: 11, fontWeight: 700 }}>{en.code || "—"}</td>
                        <td style={{ padding: "8px 10px", color: T.text, fontSize: 12, fontWeight: 700 }}>{en.name || "—"}</td>
                        <td style={{ padding: "8px 10px", color: T.text, fontSize: 11, direction: "ltr", textAlign: "center" }}>{en.phone || "—"}</td>
                        <td style={{ padding: "8px 10px", color: T.subtext, fontSize: 10 }}>{[en.city, en.notes].filter(Boolean).join(" — ") || "—"}</td>
                        <td style={{ padding: "8px 10px", color: T.text, fontSize: 11, textAlign: "center" }}>{en.currency}</td>
                        <td style={{ padding: "8px 10px", fontWeight: 800, fontSize: 13, color: Number(en.balance) >= 0 ? "#10b981" : "#ef4444", whiteSpace: "nowrap", direction: "ltr", textAlign: "left" }}>
                          {fmt(en.balance, en.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                {(() => {
                  // 🆕 ستون‌های گزارش بر اساس نوعیت حساب تعیین می‌شوند تا ستون نامربوط نمایش داده نشود
                  const isIncome = reportAcc.type === "income";
                  const isNonCashAsset = reportAcc.type === "non_cash_asset" && (reportAcc as any).auto_link !== "customers_receivable";
                  const isExpenseAccount = reportAcc.type === "expense";
                  const showExchange = !isIncome; // فروش همیشه نرخ=۱ است، ستون بی‌فایده است

                  type Col = { key: string; label: string };
                  const cols: Col[] = [
                    { key: "idx", label: "#" },
                    { key: "date", label: "تاریخ" },
                    { key: "serial", label: "شماره سند" },
                    { key: "type", label: "نوع معامله" },
                    { key: "party", label: isExpenseAccount ? "گیرنده" : "طرف حساب" },
                  ];
                  if (isIncome) {
                    cols.push(
                      { key: "notes", label: "توضیح" },
                      { key: "item_name", label: "جنس/خدمت" },
                      { key: "quantity", label: "تعداد" },
                      { key: "unit", label: "واحد" },
                      { key: "unit_price", label: "قیمت واحد" },
                      { key: "warehouse", label: "گدام" },
                      { key: "truck_plate", label: "شماره موتر" },
                      { key: "waybill_number", label: "شماره بارنامه" },
                    );
                  } else if (isNonCashAsset) {
                    cols.push(
                      { key: "category_name", label: "دسته‌بندی" },
                      { key: "notes", label: "توضیح" },
                      { key: "item_name", label: "جنس/خدمت" },
                      { key: "quantity", label: "تعداد" },
                      { key: "unit", label: "واحد" },
                      { key: "unit_price", label: "قیمت واحد" },
                      { key: "truck_plate", label: "شماره موتر" },
                      { key: "waybill_number", label: "شماره بارنامه" },
                    );
                  } else if (isExpenseAccount) {
                    cols.push(
                      { key: "category_name", label: "دسته‌بندی" },
                      { key: "notes", label: "توضیح" },
                    );
                  } else {
                    cols.push({ key: "notes", label: "توضیح" });
                  }
                  cols.push({ key: "currency", label: "ارز" }, { key: "amount", label: "مبلغ" });
                  if (showExchange) cols.push({ key: "exchange", label: "مبلغ اصلی × نرخ" }, { key: "usd", label: "معادل USD" });
                  cols.push({ key: "balance", label: "بیلانس" });

                  const idxCurrency = cols.findIndex(c => c.key === "currency");
                  const idxAmount = cols.findIndex(c => c.key === "amount");
                  const midSpan = idxCurrency - 3;   // بین «شماره سند» و «ارز»
                  const tailSpan = cols.length - idxAmount - 2; // ستون‌های نرخ/USD اگر باشند (بین مبلغ و بیلانس)

                  const openBal = reportAcc
                    ? Number((reportAcc as any).period_opening_balance ?? reportAcc.opening_balance ?? 0)
                    : 0;
                  let runBal = openBal;

                  return (
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, border: `1px solid ${T.cardBorder}` }}>
                      <thead>
                        <tr style={{ background: dark ? "#1e293b" : "#334155" }}>
                          {cols.map(c => (
                            <th key={c.key} style={{ padding: "10px 10px", textAlign: "right", fontWeight: 700, color: "#f1f5f9", whiteSpace: "nowrap", fontSize: 11 }}>
                              {c.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {/* ردیف بیلانس اولیه */}
                        <tr style={{ background: dark ? "rgba(16,185,129,0.08)" : "#f0fdf4", borderBottom: `2px solid #86efac` }}>
                          <td style={{ padding: "8px 10px", color: T.subtext, fontSize: 11 }}>—</td>
                          <td style={{ padding: "8px 10px", color: "#059669", fontSize: 11, fontWeight: 700 }}>
                            {reportAcc?.created_at ? gregorianToJalaliStr(reportAcc.created_at.split(" ")[0]) : "—"}
                          </td>
                          <td style={{ padding: "8px 10px", color: "#059669", fontSize: 11 }}>—</td>
                          <td colSpan={midSpan} style={{ padding: "8px 10px", color: "#059669", fontWeight: 800, fontSize: 12 }}>🏦 {reportDateFrom ? "بیلانس منتقل شده قبل از بازه انتخابی" : "بیلانس اولیه حساب"}</td>
                          <td style={{ padding: "8px 10px", color: T.text, fontSize: 11, textAlign: "center" }}>{reportAcc?.currency}</td>
                          <td style={{ padding: "8px 10px", fontWeight: 800, color: "#059669", fontSize: 13 }}>+{fmt(openBal)}</td>
                          {tailSpan > 0 && <td colSpan={tailSpan} style={{ padding: "8px 10px" }} />}
                          <td style={{ padding: "8px 10px", fontWeight: 800, fontSize: 13, color: "#059669" }}>{fmt(openBal)}</td>
                        </tr>
                        {reportData.length === 0 ? (
                          <tr><td colSpan={cols.length} style={{ textAlign: "center", padding: 32, color: T.subtext }}>هیچ تراکنشی ثبت نشده</td></tr>
                        ) : reportData.map((txn: any, i: number) => {
                          const amt = Math.abs(parseFloat(txn.amount || 0));
                          if (txn.direction === "in") runBal += amt; else runBal -= amt;
                          const isForeign = txn.orig_currency && txn.orig_currency !== txn.currency;
                          const cell = (key: string) => {
                            switch (key) {
                              case "idx": return i + 1;
                              case "date": return gregorianToJalaliStr(txn.date);
                              case "serial": return txn.serial || "—";
                              case "type": return txn.type;
                              case "party": return txn.party || "—";
                              case "item_name": return txn.item_name || "—";
                              case "quantity": return txn.quantity || "—";
                              case "unit": return txn.unit || "—";
                              case "unit_price": return txn.unit_price ? fmt(txn.unit_price) : "—";
                              case "warehouse": return txn.warehouse || "—";
                              case "category_name": return txn.category_name || "—";
                              case "truck_plate": return txn.truck_plate || "—";
                              case "waybill_number": return txn.waybill_number || "—";
                              case "currency": return txn.currency;
                              case "amount": return (txn.direction === "in" ? "+" : "−") + fmt(amt);
                              case "exchange": return isForeign ? `${fmt(txn.orig_amount)} ${txn.orig_currency} × ${parseFloat(txn.exchange_rate || 1).toFixed(2)}` : "—";
                              case "usd": return (txn.customer_amount && txn.customer_currency === "USD" && txn.currency !== "USD") ? `$${fmt(txn.customer_amount)}` : "—";
                              case "notes": return txn.notes || "—";
                              case "balance": return fmt(runBal);
                              default: return "—";
                            }
                          };
                          return (
                            <tr key={i} style={{ background: i % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,0.02)" : "#f8fafc") }}>
                              {cols.map(c => {
                                const isAmount = c.key === "amount";
                                const isBalance = c.key === "balance";
                                const style: React.CSSProperties = {
                                  padding: "8px 10px", fontSize: 11,
                                  color: isAmount ? (txn.direction === "in" ? "#10b981" : "#ef4444")
                                       : isBalance ? (runBal >= 0 ? "#10b981" : "#ef4444")
                                       : c.key === "serial" ? "#8b5cf6"
                                       : c.key === "notes" ? T.subtext
                                       : T.text,
                                  fontWeight: (isAmount || isBalance || c.key === "serial") ? 700 : 400,
                                  whiteSpace: (c.key === "party" || c.key === "notes") ? "normal" : "nowrap",
                                  wordBreak: (c.key === "party" || c.key === "notes") ? "break-word" : undefined,
                                  maxWidth: c.key === "party" ? 200 : c.key === "notes" ? 220 : undefined,
                                  textAlign: ["currency", "amount", "exchange", "usd", "balance", "quantity", "unit", "unit_price"].includes(c.key) ? "center" : undefined,
                                };
                                return <td key={c.key} style={style}>{cell(c.key)}</td>;
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  );
                })()}
              </div>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
            <button onClick={handleExcelDownload} disabled={excelLoading} style={{
              display: "flex", alignItems: "center", gap: 8, padding: "10px 24px",
              borderRadius: 10, border: "none", cursor: excelLoading ? "not-allowed" : "pointer",
              background: excelLoading ? "#9ca3af" : "linear-gradient(135deg,#059669,#047857)", color: "#fff",
              fontSize: 13, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
            }}>
              📊 {excelLoading ? "در حال تولید..." : "دانلود اکسل"}
            </button>
            <button onClick={printReport} style={{
              display: "flex", alignItems: "center", gap: 8, padding: "10px 24px",
              borderRadius: 10, border: "none", cursor: "pointer",
              background: "linear-gradient(135deg,#8b5cf6,#7c3aed)", color: "#fff",
              fontSize: 13, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
            }}>
              <Printer size={15} /> چاپ گزارش
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ---- کامپوننت‌های کمکی ----

function Modal({ title, onClose, dark, T, children, wide }: any) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(15,23,42,0.72)", backdropFilter: "blur(6px)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20,
      animation: "fadeIn 0.15s ease",
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(16px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
      `}</style>
      <div style={{
        background: dark ? "#1e293b" : "#fff", borderRadius: 20,
        width: "100%", maxWidth: wide ? 920 : 560,
        maxHeight: "88vh", overflow: "hidden", display: "flex", flexDirection: "column",
        boxShadow: dark ? "0 30px 70px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06)" : "0 30px 70px rgba(15,23,42,0.25), 0 0 0 1px rgba(15,23,42,0.04)",
        animation: "slideUp 0.22s cubic-bezier(0.16,1,0.3,1)",
      }}>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "20px 28px", flexShrink: 0,
          background: dark ? "linear-gradient(135deg,#1e293b,#0f172a)" : "linear-gradient(135deg,#f8fafc,#eef2ff)",
          borderBottom: `1px solid ${T.cardBorder}`,
        }}>
          <h3 style={{ margin: 0, fontSize: 16.5, fontWeight: 800, color: T.text, letterSpacing: "-0.2px" }}>{title}</h3>
          <button onClick={onClose} style={{
            background: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.05)", border: "none",
            borderRadius: 9, cursor: "pointer", color: T.subtext, padding: 6,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <X size={18} />
          </button>
        </div>
        <div style={{ padding: "24px 28px", overflow: "auto" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

const Label = ({ T, children }: any) => (
  <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: T.subtext, marginBottom: 6 }}>
    {children}
  </label>
);

const BtnCancel = ({ onClick, T }: any) => (
  <button onClick={onClick} style={{
    padding: "10px 20px", borderRadius: 10, border: `1.5px solid ${T.cardBorder}`,
    background: "transparent", color: T.text, cursor: "pointer",
    fontSize: 13, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
  }}>انصراف</button>
);

const BtnSave = ({ onClick, loading, color, label }: any) => (
  <button onClick={onClick} disabled={loading} style={{
    padding: "10px 24px", borderRadius: 10, border: "none",
    background: loading ? "#9ca3af" : `linear-gradient(135deg,${color},${color}cc)`,
    color: "#fff", cursor: loading ? "not-allowed" : "pointer",
    fontSize: 13, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
  }}>
    {loading ? "در حال ذخیره..." : label}
  </button>
);

const iconBtnStyle = (color: string): React.CSSProperties => ({
  width: 30, height: 30, borderRadius: 9, border: "none",
  background: `${color}18`, color, cursor: "pointer",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  transition: "transform 0.15s, background 0.15s",
});